/**
 * First-run wizard step ids and progress helpers (#1853, #1498).
 *
 * Progress is a list of step ids, not a count, so adding a step later cannot
 * silently mark it done for a workshop that finished an earlier version.
 * The original three ids (`workshop`, `staff`, `lifts`) stay valid forever.
 */

export const ONBOARDING_STEPS = ['workshop', 'hours', 'staff', 'lifts', 'services'] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/** Original #1853 ids. Stored blobs that only contain these still parse. */
export const LEGACY_ONBOARDING_STEPS = ['workshop', 'staff', 'lifts'] as const;

export const OPTIONAL_ONBOARDING_STEPS = ['hours', 'staff', 'lifts', 'services'] as const;

export interface OnboardingState {
  completedSteps: OnboardingStep[];
  completedAt?: string | null;
  skippedAt?: string | null;
}

export const EMPTY_ONBOARDING_STATE: OnboardingState = { completedSteps: [] };

/** Prompt hide-window after skip-all. Session dismiss is separate and shorter. */
export const ONBOARDING_SNOOZE_MS = 24 * 60 * 60 * 1000;

export const ONBOARDING_STEP_PERMISSION: Record<OnboardingStep, string> = {
  workshop: 'settings.edit_workshop',
  hours: 'settings.edit_workshop',
  staff: 'settings.manage_users',
  lifts: 'equipment.manage',
  services: 'settings.manage_services',
};

export function isOnboardingStep(value: string): value is OnboardingStep {
  return (ONBOARDING_STEPS as readonly string[]).includes(value);
}

export function isOptionalOnboardingStep(step: OnboardingStep): boolean {
  return (OPTIONAL_ONBOARDING_STEPS as readonly OnboardingStep[]).includes(step);
}

/** Narrow the untyped settings bag without trusting its shape. */
export function readOnboardingState(raw: unknown): OnboardingState {
  if (typeof raw !== 'object' || raw === null) return EMPTY_ONBOARDING_STATE;

  const candidate = raw as Partial<OnboardingState>;
  const steps = Array.isArray(candidate.completedSteps) ? candidate.completedSteps : [];

  return {
    // A step id the client no longer knows is dropped rather than rendered:
    // an unknown id must never make the wizard look further along than it is.
    completedSteps: steps.filter((s): s is OnboardingStep =>
      typeof s === 'string' && isOnboardingStep(s),
    ),
    completedAt: candidate.completedAt ?? null,
    skippedAt: candidate.skippedAt ?? null,
  };
}

/** First incomplete step, or the last step when every id is already recorded. */
export function resumeStepIndex(completedSteps: readonly OnboardingStep[]): number {
  const idx = ONBOARDING_STEPS.findIndex((s) => !completedSteps.includes(s));
  return idx === -1 ? ONBOARDING_STEPS.length - 1 : idx;
}

export function isOnboardingSnoozed(
  skippedAt: string | null | undefined,
  now = Date.now(),
): boolean {
  if (!skippedAt) return false;
  const ts = Date.parse(skippedAt);
  if (Number.isNaN(ts)) return false;
  return now - ts < ONBOARDING_SNOOZE_MS;
}

export function nextOnboardingState(
  state: OnboardingState,
  step: OnboardingStep,
  nowIso: string,
): OnboardingState | null {
  if (state.completedSteps.includes(step)) return null;

  const completedSteps = [...state.completedSteps, step];
  const allDone = ONBOARDING_STEPS.every((s) => completedSteps.includes(s));

  return {
    completedSteps,
    completedAt: allDone ? nowIso : null,
    skippedAt: state.skippedAt ?? null,
  };
}

export function skippedOnboardingState(state: OnboardingState, nowIso: string): OnboardingState {
  return {
    completedSteps: state.completedSteps,
    completedAt: state.completedAt ?? null,
    skippedAt: nowIso,
  };
}

export function resumedOnboardingState(state: OnboardingState): OnboardingState {
  return {
    completedSteps: state.completedSteps,
    completedAt: state.completedAt ?? null,
    skippedAt: null,
  };
}

/**
 * One write that records every still-open optional step. Finish cannot call
 * `completeStep` in a loop: each call would snapshot the same state and the
 * last PATCH would drop the others.
 */
export function finishOnboardingState(state: OnboardingState, nowIso: string): OnboardingState {
  const completedSteps = [...state.completedSteps];
  for (const step of OPTIONAL_ONBOARDING_STEPS) {
    if (!completedSteps.includes(step)) completedSteps.push(step);
  }
  const allDone = ONBOARDING_STEPS.every((s) => completedSteps.includes(s));
  return {
    completedSteps,
    completedAt: allDone ? nowIso : state.completedAt ?? null,
    skippedAt: state.skippedAt ?? null,
  };
}
